import { createSlice } from '@reduxjs/toolkit';
import Config from '../../common/js/utils/Config';
import { getTpAuthorization, setTpHeaders } from '../../common/js/utils/Helpers';
import { adminxApi } from './adminxApi';
import Session from '../../common/js/utils/Session';

const initialState = {
  companyData: {
    displayName: '',
    address: {
      streetAddressLine1: '',
      streetAddressLine2: '',
      postalCode: '',
      region: '',
      country: '',
    },
    tiemzone: '',
    size: '',
    contactData: {
      name: '',
      lastName: '',
      emailId: '',
      phoneNumber: '',
    },
  },
};

const getUrl = (type, param = {}) => {
  param.dependency = param.dependency || '';
  const isDemo = Config.isDemo(param.dependency);
  if (isDemo) {
    switch (type) {
      case 'updateTenantPreference':
        return Config.apiPath('demo', 'organization', 'updateTenantPreference.json');
      case 'updateUserPreference':
        return Config.apiPath('demo', 'organization', 'updateUserPreference.json');
      case 'addTrialPlan':
        return Config.apiPath('demo', 'organization', 'addTrialPlan');
      case 'getCompanyDetails':
        return Config.apiPath('demo', 'organization', 'getCompanyDetails');
      case 'getTenantPreference':
        return Config.apiPath('demo', 'organization', 'getTenantPreference');
      case 'getPermissionDetails':
        return Config.apiPath('demo', 'organization', 'getPermissionDetails');
      case 'onboardStatus':
        return Config.apiPath('demo', 'organization', 'onboardStatusResponse');
      case 'createCompanyOneCloud':
        return Config.apiPath('demo', 'organization', 'createCompanyOneCloud');
      case 'initSelfSignupUS':
      case 'initSelfSignupEU':
      case 'initSelfSignupAU':
        return Config.apiPath('demo', 'organization', 'initSelfSignupOC');
      default:
        return `${Config.getAssetBase()}data/`;
    }
  } else {
    switch (type) {
      case 'updateTenantPreference':
        return Config.apiPath('gandalf', 'updateTenantPreference', param.tenantId, param.key);
      case 'updateUserPreference':
        return Config.apiPath('gandalf', 'updateUserPreference', param.userId, param.key);
      case 'addTrialPlan':
        return Config.apiPath('gandalf', 'addTrialPlan', param.tenantId, param.planName);
      case 'onboardStatus':
        return Config.apiPath('idm', 'onboardingStatus', param.tenant, param.status);
      case 'getCompanyDetails':
        return Config.apiPath('idm', 'companyDetails', param.tenantId);
      case 'getTenantPreference':
        return Config.apiPath('idm', 'getTenantPreference', param.tenantId);
      case 'getPermissionDetails':
        return Config.apiPath('gandalf', 'abilities', '1.0');
      case 'createCompanyOneCloud':
        return Config.apiPath('idm', 'createCompanyOneCloud');
      case 'initSelfSignupUS':
        return Config.apiPath('daasUs', 'initSelfSignupOC');
      case 'initSelfSignupEU':
        return Config.apiPath('daasEu', 'initSelfSignupOC');
      case 'initSelfSignupAU':
        return Config.apiPath('daasAu', 'initSelfSignupOC');
      case 'updateTenantConsent':
        return Config.apiPath('gandalf', 'telemetryConsent', param.tenantId, param.key);
      default:
        return '';
    }
  }
};

const extendedApi = adminxApi.injectEndpoints({
  endpoints: (builder) => ({
    updateTenantConsent: builder.mutation({
      query: (param = {}) => ({
        url: getUrl('updateTenantConsent', { ...param }),
        method: Config.isDemo() ? 'GET' : 'POST',
        headers: getTpAuthorization(),
        ...(Config.isDemo()
          ? {}
          : {
              body: {
                ...param,
              },
            }),
      }),
    }),
    updateCompany: builder.mutation({
      query: (param = {}) => ({
        url: getUrl('getCompanyDetails', { tenantId: param.tenantId, dependency: 'tp' }),
        method: Config.isDemo('tp') ? 'GET' : 'PATCH',
        ...(!Config.isDemo('tp')
          ? {
              headers: getTpAuthorization(),
            }
          : {}),
        ...(Config.isDemo('tp')
          ? {}
          : {
              body: {
                ...param.data,
              },
            }),
      }),
    }),
    updateOnboardStatus: builder.mutation({
      query: (param = {}) => ({
        url: getUrl('onboardStatus', { dependency: 'tp', ...param }),
        method: Config.isDemo('tp') ? 'GET' : 'PATCH',
        ...(!Config.isDemo('tp')
          ? {
              headers: getTpAuthorization(),
            }
          : {}),
      }),
    }),
    getTenantPreference: builder.mutation({
      query: (param = {}) => ({
        url: getUrl('getTenantPreference', { ...param, dependency: 'tp' }),
        method: Config.isDemo() ? 'GET' : 'POST',
        headers: getTpAuthorization(),
        ...(Config.isDemo()
          ? {}
          : {
              body: JSON.stringify(['wfc_waiting', 'enforce_azure_login']),
            }),
      }),
    }),
    updateTenantPreference: builder.mutation({
      query: (param = {}) => ({
        url: getUrl('updateTenantPreference', { ...param }),
        method: Config.isDemo() ? 'GET' : 'POST',
        headers: getTpAuthorization(),
        ...(Config.isDemo()
          ? {}
          : {
              body: param,
            }),
      }),
    }),
    updateUserPreference: builder.mutation({
      query: (param = {}) => ({
        url: getUrl('updateUserPreference', { ...param }),
        method: Config.isDemo() ? 'GET' : 'POST',
        headers: getTpAuthorization(),
        ...(Config.isDemo()
          ? {}
          : {
              body: param,
            }),
      }),
    }),
    getCompanyDetails: builder.mutation({
      query: (param = {}) => ({
        url: getUrl('getCompanyDetails', { ...param, dependency: 'tp' }),
        method: 'GET',
        headers: getTpAuthorization(),
      }),
    }),
    getPermissionDetails: builder.mutation({
      query: () => ({
        url: getUrl('getPermissionDetails'),
        method: 'GET',
        headers: getTpAuthorization(),
      }),
    }),
    addTrialPlan: builder.mutation({
      query: (param = {}) => ({
        url: getUrl('addTrialPlan', { ...param }),
        method: Config.isDemo() ? 'GET' : 'POST',
        headers: getTpAuthorization(),
      }),
    }),
    createCompanyOneCloud: builder.mutation({
      query: (param = {}) => ({
        url: getUrl('createCompanyOneCloud'),
        method: Config.isDemo() ? 'GET' : 'POST',
        headers: {
          Authorization: 'Bearer ' + Session.getJWTTokenForOrgLess(),
          ...setTpHeaders({}),
        },
        ...(Config.isDemo()
          ? {}
          : {
              body: param,
            }),
      }),
    }),
    initSelfSignupOC: builder.mutation({
      query: (param = {}) => ({
        url: getUrl(
          param.region === 'EU' ? 'initSelfSignupEU' : param.region === 'AU' ? 'initSelfSignupAU' : 'initSelfSignupUS',
          { ...param },
        ),
        method: Config.isDemo() ? 'GET' : 'POST',
        headers: {
          Authorization: 'Bearer ' + Session.getJWTTokenForOrgLess(),
          ...setTpHeaders({}),
        },
        ...(Config.isDemo()
          ? {}
          : {
              body: param,
            }),
      }),
    }),
  }),
});

export const companyInfoReducer = createSlice({
  name: 'companyInfo',
  initialState,
  reducers: {
    setCompanyData: (state, action) => {
      state.companyData = action.payload;
    },
  },
});

export const { setCompanyData } = companyInfoReducer.actions;
export const {
  useUpdateCompanyMutation,
  useUpdateOnboardStatusMutation,
  useGetCompanyDetailsMutation,
  useUpdateTenantPreferenceMutation,
  useUpdateUserPreferenceMutation,
  useGetTenantPreferenceMutation,
  useGetPermissionDetailsMutation,
  useAddTrialPlanMutation,
  useCreateCompanyOneCloudMutation,
  useInitSelfSignupOCMutation,
  useUpdateTenantConsentMutation,
} = extendedApi;
